import gql from 'graphql-tag';

import { Query, Mutation, QueryRegistry } from 'core';

import { CONTENT_FIELDS } from 'apps/content/gql';
import { USER_ERROR_FIELDS } from 'core/errors';

export const ISSUE_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment issueFields on Issue {
    id
    canUpdate
    canDelete
    canAddDeleteLabels
    number
    title
    slug
    status
    description
    content {
      ...contentFields
    }
    labels {
      id
      name
      color
    }
    creatorProfile {
      id
      username
      avatar {
        id
        path
        url
      }
      imageFallbackChar
    }
    dateCreated
    lastUpdated
    commenters {
      id
      username
      avatar {
        id
        path
        url
      }
      imageFallbackChar
    }
    inSpace {
      id
      spaceType
      whichTypes
      content {
        id
        title
        type
        slug
        avatar {
          id
          path
          url
          completed
          private
        }
      }
      parent {
        id
        spaceType
        content {
          id
          slug
          whitelabel
        }
      }
    }
    comments {
      totalCount
    }
    assignees {
      id
      username
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    tags {
      id
      name
    }
  }
`;

export const ISSUE_CONNECTION_FIELDS = gql`
  ${ISSUE_FIELDS}
  fragment issueConnectionFields on IssueConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...issueFields
      }
    }
  }
`;

export const ISSUE_MUTATION = gql`
  ${ISSUE_FIELDS}
  ${USER_ERROR_FIELDS}
  mutation NewIssue($issueInput: IssueInput) {
    issue(issueData: $issueInput) {
      issue {
        ...issueFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class IssueMutation extends Mutation {
  static conf = {
    GQL: ISSUE_MUTATION,
    inputName: 'issueInput',
    resultPath: 'issue.issue',
    errorPath: 'issue.userErrors',
  };
}

export const LABEL_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation NewLabel($labelInput: LabelInput) {
    label(labelData: $labelInput) {
      label {
        id
        name
        color
        restricted
        project {
          tracker {
            id
            labels {
              id
              name
              color
            }
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class LabelMutation extends Mutation {
  static conf = {
    GQL: LABEL_MUTATION,
    inputName: 'labelInput',
    resultPath: 'label.label',
    errorPath: 'label.userErrors',
  };
}

export const ISSUE_TRACKER_QUERY = gql`
  ${USER_ERROR_FIELDS}
  ${ISSUE_CONNECTION_FIELDS}
  query IssueTracker(
    $space: String
    $slug: String
    $cursor: String
    $sortBy: String
    $batchSize: Int
    $filterBy: [[String]]
  ) {
    project(space: $space, slug: $slug) {
      result {
        id
        tracker {
          id
          labels {
            id
            canUpdate
            canDelete
            name
            color
          }
          issues(
            first: $batchSize
            after: $cursor
            sortBy: $sortBy
            filterBy: $filterBy
          ) {
            ...issueConnectionFields
          }
          openIssuesCount
          closedIssuesCount
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class IssueTrackerQuery extends Query {
  static conf = {
    GQL: ISSUE_TRACKER_QUERY,
    varNames: ['space', 'slug', 'cursor', 'sortBy', 'batchSize', 'filterBy'],
    resultPath: 'project.result.tracker',
    errorPath: 'project.userErrors',
  };
}

const TRACKER_LABELS_QUERY = gql`
  ${USER_ERROR_FIELDS}
  query TrackerLabels($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      result {
        id
        tracker {
          id
          labels {
            id
            name
            color
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class TrackerLabelsQuery extends Query {
  static conf = {
    GQL: TRACKER_LABELS_QUERY,
    varNames: ['space', 'slug'],
    resultPath: 'project.result.tracker.labels',
    errorPath: 'project.userErrors',
  };
}
QueryRegistry.add('issueTracker.TrackerLabelsQuery', TrackerLabelsQuery);

export const PROJECT_ISSUES = gql`
  ${USER_ERROR_FIELDS}
  ${ISSUE_CONNECTION_FIELDS}
  query Issues(
    $space: String
    $slug: String
    $cursor: String
    $sortBy: String
    $batchSize: Int
    $filterBy: [[String]]
  ) {
    project(space: $space, slug: $slug) {
      result {
        id
        tracker {
          id
          issues(
            first: $batchSize
            after: $cursor
            sortBy: $sortBy
            filterBy: $filterBy
          ) {
            ...issueConnectionFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProjectIssuesQuery extends Query {
  static conf = {
    GQL: PROJECT_ISSUES,
    varNames: ['space', 'slug', 'cursor', 'sortBy', 'batchSize', 'filterBy'],
    resultPath: 'project.result.tracker.issues',
  };
}

export const ISSUE = gql`
  ${ISSUE_FIELDS}
  ${USER_ERROR_FIELDS}
  query Issue($space: String, $slug: String, $issueSlug: String!) {
    project(space: $space, slug: $slug) {
      result {
        id
        tracker {
          id
          issue(issueSlug: $issueSlug) {
            ...issueFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class IssueQuery extends Query {
  static conf = {
    GQL: ISSUE,
    varNames: ['space', 'slug', 'issueSlug'],
    resultPath: 'project.result.tracker.issue',
  };
}

export const ISSUES = gql`
  ${ISSUE_FIELDS}
  ${USER_ERROR_FIELDS}
  query Issues(
    $tag: String
    $filterBy: [[String]]
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    issues(
      tag: $tag
      filterBy: $filterBy
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
    ) {
      result {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          cursor
          node {
            ...issueFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class IssuesQuery extends Query {
  static conf = {
    GQL: ISSUES,
    varNames: ['tag', 'filterBy', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'issues.result',
    errorPath: 'issues.userErrors',
  };
}
